import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AnalyticsWarehousePicker from "../../components/analytics-warehouse-picker";
import ClientAutocomplete from "../../components/clientAutocomplete";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import SalesPerformancePageLoading from "../../components/metrics/SalesPerformancePageLoading";
import {
  MONO_FONT,
  PAGE_FONT,
  PillTabs,
  headCellSx,
  numCellSx,
  textCellSx,
  useMetricsUi,
} from "../../components/metrics/metricsUi";
import { isMobile } from "../../globalStyles/mobile";
import { fieldSx } from "../manage-catalog/constants";
import { inventoryRiskService } from "../../utils/services/inventoryRiskService";
import { analyticsWarehouseService } from "../../utils/services/analyticsWarehouseService";
import { selectableWarehouse } from "../../utils/inventoryHealthPageDefaults";
import {
  DEFAULT_SORT,
  DEFAULT_VIEW,
  RISK_SEGMENTS,
  VIEW_TABS,
  buildInventoryRiskParams,
  fmtDays,
  fmtMoney,
  fmtUnits,
  hasSkuFiltersFor,
  inventoryRiskErrorMessage,
  riskCardsFor,
  riskSegmentsFor,
  toggleRisk,
} from "../../utils/inventoryRiskPageDefaults";
import ProductRiskDrawer from "./ProductRiskDrawer";
import { RiskCounts, RiskProfileBar } from "./RiskProfile";

const FIELD_HEIGHT = 40;
const SEARCH_DEBOUNCE_MS = 300;

const fieldControlSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    height: FIELD_HEIGHT,
    fontFamily: PAGE_FONT,
    fontSize: 14,
  },
};

const SEGMENT_COLOR = Object.fromEntries(
  RISK_SEGMENTS.map((segment) => [segment.key, segment.color]),
);

// The per-bucket SKU counts live in the Risk Profile column (bar + counts),
// so the table fits without scrolling Revenue at Risk out of view.
// Fixed layout: every column but Product has a set width (sized for its header
// plus the sort icon); Product takes the rest and wraps to two lines.
const COLUMNS = [
  { id: "product", label: "Product", sort: "product" },
  { id: "profile", label: "SKU Risk Profile", width: 250, tip: "SKUs per risk bucket, worst first" },
  {
    id: "skus",
    label: "SKUs",
    sort: "skus",
    num: true,
    width: 76,
    tip: "Active SKUs (in stock or sold in the 30-day window)",
  },
  {
    id: "on_hand",
    label: "On Hand",
    sort: "on_hand",
    num: true,
    width: 96,
    tip: "Current (live) units on hand at this warehouse",
  },
  {
    id: "monthly_units",
    label: "30d Sales",
    sort: "monthly_units",
    num: true,
    width: 112,
    tip: "Units sold in the 30-day window",
  },
  {
    id: "days_cover",
    label: "Avg Days",
    sort: "days_cover",
    num: true,
    width: 104,
    tip: "Days of cover pooled across the product's SKUs: on hand ÷ daily sales",
  },
  {
    id: "revenue",
    label: "30d Revenue",
    sort: "revenue",
    num: true,
    width: 128,
    tip: "Revenue sold in the 30-day window",
  },
  {
    id: "revenue_at_risk",
    label: "Rev. at Risk",
    sort: "revenue_at_risk",
    num: true,
    width: 132,
    tip: "30-day revenue of Critical and At Risk SKUs",
  },
];
// Below this the table scrolls horizontally rather than squeezing Product further.
const TABLE_MIN_WIDTH = 1100;
const CELL_PX = 1.25;

const eyebrowSx = (colors) => ({
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: colors.textSecondary,
});

const formatDay = (value) => {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

const StatePanel = ({ tone = "info", eyebrow, title, body, ui }) => {
  const Icon = tone === "error" ? ErrorOutlineIcon : InfoOutlinedIcon;
  const accent = tone === "error" ? ui.danger : ui.accent;
  return (
    <Box
      sx={{
        ...ui.card,
        px: 3,
        py: 4.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Icon sx={{ fontSize: 24, color: accent, mb: 1.25 }} />
      {eyebrow ? (
        <Typography sx={{ ...eyebrowSx(ui.colors), color: accent, mb: 0.85 }}>
          {eyebrow}
        </Typography>
      ) : null}
      <Typography
        sx={{ fontSize: 15, fontWeight: 650, color: ui.colors.textPrimary, maxWidth: 560 }}
      >
        {title}
      </Typography>
      {body ? (
        <Typography
          sx={{ fontSize: 13, color: ui.colors.textSecondary, maxWidth: 560, mt: 0.85 }}
        >
          {body}
        </Typography>
      ) : null}
    </Box>
  );
};

const KpiCard = ({ label, value, hint, color, ui, onClick, active = false }) => (
  <Box
    {...(onClick
      ? {
          component: "button",
          type: "button",
          onClick,
          "aria-pressed": active,
        }
      : {})}
    sx={{
      ...ui.card,
      borderLeft: `4px solid ${color}`,
      p: "13px 15px 12px",
      height: "100%",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      textAlign: "left",
      fontFamily: "inherit",
      ...(onClick
        ? {
            cursor: "pointer",
            "&:hover": { backgroundColor: ui.surfaceAlt },
          }
        : {}),
      ...(active ? { boxShadow: `0 0 0 2px ${color}` } : {}),
    }}
  >
    <Typography sx={{ ...eyebrowSx(ui.colors), lineHeight: 1.35 }}>{label}</Typography>
    <Typography
      sx={{
        fontFamily: MONO_FONT,
        fontSize: 23,
        fontWeight: 650,
        fontVariantNumeric: "tabular-nums",
        color: ui.colors.textPrimary,
        mt: 0.75,
        lineHeight: 1.15,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{ fontSize: 11, color: ui.colors.textTertiary, mt: "auto", pt: 0.9, lineHeight: 1.35 }}
    >
      {hint}
    </Typography>
  </Box>
);

const FilterChip = ({ segment, active, onClick, ui }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-pressed={active}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.7,
      fontFamily: "inherit",
      fontSize: 12.5,
      fontWeight: active ? 650 : 500,
      px: 1.25,
      py: 0.6,
      borderRadius: "16px",
      cursor: "pointer",
      whiteSpace: "nowrap",
      border: `1px solid ${active ? segment.color : ui.colors.border}`,
      backgroundColor: active ? `${segment.color}1F` : ui.colors.bgPaper,
      color: active ? ui.colors.textPrimary : ui.colors.textSecondary,
      "&:hover": { backgroundColor: active ? `${segment.color}2E` : ui.surfaceAlt },
    }}
  >
    <Box
      component="span"
      sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: segment.color }}
    />
    {segment.short}
  </Box>
);

const InventoryRiskPage = () => {
  const ui = useMetricsUi();
  const { colors, darkMode } = ui;
  const mobile = isMobile();
  const storedClientId = localStorage.getItem("client_id");
  const [selectedClientData, setSelectedClientData] = useState(null);
  const clientId = selectedClientData?._id || storedClientId;

  // Exactly one warehouse — risk is never combined across warehouses. Starts
  // from the client's default analytics warehouse (else the first offered);
  // the user can switch to any other analytics-visible warehouse.
  const [warehouse, setWarehouse] = useState(null);
  const warehouseId = warehouse?._id || null;

  const [view, setView] = useState(DEFAULT_VIEW);
  const [risks, setRisks] = useState([]);
  const [drawerProduct, setDrawerProduct] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Results are stamped with the warehouse they were requested for so a
  // superseded response can never render another warehouse's numbers.
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestSeq = useRef(0);

  useEffect(() => {
    setWarehouse(null);
    setResult(null);
    setDrawerProduct(null);
    if (!clientId) return undefined;
    let cancelled = false;
    analyticsWarehouseService
      .fetchDefault(clientId)
      .then((preferred) => {
        // Never override a warehouse the user already picked.
        if (!cancelled && preferred) {
          setWarehouse((current) => current || selectableWarehouse(preferred));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!clientId || !warehouseId) {
      requestSeq.current += 1;
      setLoading(false);
      return undefined;
    }
    const seq = ++requestSeq.current;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    inventoryRiskService
      .fetch(
        buildInventoryRiskParams({
          clientId: storedClientId ? null : clientId,
          warehouseId,
          page,
          rowsPerPage,
          sort: sort.field,
          dir: sort.dir,
          search,
          view,
          risks,
        }),
        { signal: controller.signal },
      )
      .then((data) => {
        if (seq !== requestSeq.current) return;
        setResult({ warehouseId, data });
      })
      .catch((err) => {
        if (seq !== requestSeq.current || controller.signal.aborted) return;
        setResult(null);
        setError(inventoryRiskErrorMessage(err));
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
    return () => controller.abort();
  }, [clientId, storedClientId, warehouseId, page, rowsPerPage, sort, search, view, risks]);

  const payload = result && result.warehouseId === warehouseId ? result.data : null;
  const summary = payload?.summary;
  // The client's own risk buckets (Client Config → Dashboard settings).
  const riskCards = riskCardsFor(payload?.risk_bands);
  const riskSegments = riskSegmentsFor(payload?.risk_bands);
  const skuFilters = hasSkuFiltersFor(payload?.risk_bands);
  const rows = payload?.rows || [];
  const dataQuality = payload?.data_quality;

  const toggleSort = (field) => {
    setSort((current) =>
      current.field === field
        ? { field, dir: current.dir === "desc" ? "asc" : "desc" }
        : { field, dir: field === "product" ? "asc" : "desc" },
    );
    setPage(0);
  };

  const onWarehouseChange = (item) => {
    setWarehouse(selectableWarehouse(item));
    setResult(null);
    setDrawerProduct(null);
    setPage(0);
  };

  const applyFilters = (nextView, nextRisks) => {
    setView(nextView);
    setRisks(nextRisks);
    setPage(0);
  };

  // A card shows every product with a SKU in that bucket, whatever the view;
  // clicking the active card again goes back to the default view.
  const cardActive = (key) => view === "all" && risks.length === 1 && risks[0] === key;
  const onCardClick = (key) =>
    cardActive(key) ? applyFilters(DEFAULT_VIEW, []) : applyFilters("all", [key]);
  const filtered = Boolean(search) || view !== "all" || risks.length > 0;

  const cellValue = (row, column) => {
    const productName = row.product_name || `Product ${String(row.product_id).slice(-6)}`;
    switch (column.id) {
      case "product":
        return (
          <Box
            title={productName}
            sx={{
              fontWeight: 600,
              lineHeight: 1.35,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              overflowWrap: "anywhere",
            }}
          >
            {productName}
          </Box>
        );
      case "days_cover":
        return row.days_cover == null ? (
          <Box component="span" sx={{ color: colors.textTertiary }}>
            {fmtDays(null)}
          </Box>
        ) : (
          fmtDays(row.days_cover)
        );
      case "profile":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RiskProfileBar row={row} ui={ui} width={56} />
            <RiskCounts row={row} ui={ui} />
          </Box>
        );
      case "revenue":
        return fmtMoney(row[column.id]);
      case "revenue_at_risk":
        return row.revenue_at_risk > 0 ? (
          <Box component="span" sx={{ fontWeight: 700, color: colors.textPrimary }}>
            {fmtMoney(row.revenue_at_risk)}
          </Box>
        ) : (
          fmtMoney(row.revenue_at_risk)
        );
      default:
        return fmtUnits(row[column.id]);
    }
  };

  const showInitialLoading = loading && !payload;

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: darkMode ? colors.bgPrimary : "#F5F6F8",
        fontFamily: PAGE_FONT,
      }}
    >
      {loading && (
        <LinearProgress sx={{ position: "sticky", top: 0, zIndex: 10, height: 3 }} />
      )}
      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          padding: mobile ? "14px 12px 40px" : "22px 28px 56px",
        }}
      >
        <AnalysisPageTopbar
          badge="IR"
          title="Inventory Risk by Product"
          clientName={selectedClientData?.business_name || selectedClientData?.name}
          right={
            !storedClientId ? (
              <ClientAutocomplete
                width={220}
                selectedClient={selectedClientData}
                onClientChange={setSelectedClientData}
              />
            ) : null
          }
        />

        <Box sx={{ ...ui.card, p: mobile ? "14px" : "16px 18px 18px", mb: 2.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: mobile ? "1fr" : "minmax(260px, 360px) 1fr",
              gap: 2,
              alignItems: "end",
            }}
          >
            <Box>
              <Typography sx={{ ...eyebrowSx(colors), mb: 0.65 }}>
                Warehouse (required)
              </Typography>
              <AnalyticsWarehousePicker
                clientId={clientId}
                mode="single"
                allowAll={false}
                value={warehouse}
                onChange={onWarehouseChange}
                placeholder="Select a warehouse"
                disabled={!clientId}
                width="100%"
                height={FIELD_HEIGHT}
                triggerSx={{ fontFamily: PAGE_FONT, fontSize: 14, fontWeight: 400 }}
              />
            </Box>
            {payload ? (
              <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, lineHeight: 1.55 }}>
                <strong>{payload.warehouse?.name}</strong> · 30-day sales{" "}
                {payload.window
                  ? `${formatDay(payload.window.start)} → ${formatDay(payload.window.end)}`
                  : "— no order history"}{" "}
                (window ends on the client&apos;s latest order, not today)
              </Typography>
            ) : null}
          </Box>
          {payload?.window?.is_stale ? (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mt: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: "10px",
                backgroundColor: ui.warningBg,
              }}
            >
              <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: ui.warning, mt: "1px" }} />
              <Typography sx={{ fontSize: 12.5, color: ui.warning, fontWeight: 500 }}>
                Sales data last updated {formatDay(payload.window.end)} — risk may be out of
                date.
              </Typography>
            </Box>
          ) : null}
        </Box>

        {!clientId && (
          <StatePanel
            ui={ui}
            eyebrow="Client required"
            title="Select a client to view inventory risk."
          />
        )}

        {clientId && !warehouseId && (
          <StatePanel
            ui={ui}
            eyebrow="Warehouse required"
            title="Select a warehouse to load its inventory risk."
            body="Risk is calculated for one warehouse at a time. Inventory is never combined across warehouses."
          />
        )}

        {clientId && warehouseId && error && (
          <StatePanel ui={ui} tone="error" eyebrow="Could not load" title={error} />
        )}

        {clientId && warehouseId && !error && showInitialLoading && (
          <SalesPerformancePageLoading
            title={`Loading inventory risk for ${warehouse?.warehouse_code || "warehouse"}…`}
            kpiCount={6}
          />
        )}

        {payload && summary && !error && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: mobile
                  ? "repeat(2, minmax(0, 1fr))"
                  : `repeat(${riskCards.length + 1}, minmax(0, 1fr))`,
                gap: 1.5,
                mb: 2.5,
              }}
            >
              {riskCards.map((card) => (
                <KpiCard
                  key={card.key}
                  ui={ui}
                  color={SEGMENT_COLOR[card.key]}
                  label={card.label}
                  value={`${fmtUnits(summary[card.key])} SKUs`}
                  hint={`in ${fmtUnits(summary.products_with?.[card.key])} products — ${card.action}`}
                  onClick={() => onCardClick(card.key)}
                  active={cardActive(card.key)}
                />
              ))}
              <KpiCard
                ui={ui}
                color={ui.accent}
                label="Products"
                value={fmtUnits(summary.products)}
                hint={`${fmtUnits(summary.skus)} SKUs · ${fmtUnits(summary.on_hand)} on hand · ${fmtUnits(summary.monthly_units)} units/30d`}
              />
            </Box>

            <Box sx={{ ...ui.card, overflow: "hidden" }}>
              <Box sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                    mb: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>
                    Highest Revenue at Risk
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>
                    {fmtMoney(summary.revenue_at_risk)} at risk (Critical + At Risk SKUs) of{" "}
                    {fmtMoney(summary.revenue)} 30-day revenue
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <PillTabs
                      tabs={VIEW_TABS.map((tab) => ({
                        ...tab,
                        badge:
                          tab.value === "attention"
                            ? fmtUnits(summary.attention_products)
                            : fmtUnits(summary.products),
                        tooltip:
                          tab.value === "attention"
                            ? "Products with at least one Out of Stock, Critical or At Risk SKU"
                            : undefined,
                      }))}
                      value={view}
                      onChange={(value) => applyFilters(value, risks)}
                    />
                    <Box
                      role="group"
                      aria-label="Has SKUs in"
                      sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}
                    >
                      <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, mr: 0.25 }}>
                        Has SKUs in
                      </Typography>
                      {skuFilters.map((segment) => (
                        <FilterChip
                          key={segment.key}
                          ui={ui}
                          segment={segment}
                          active={risks.includes(segment.key)}
                          onClick={() => applyFilters(view, toggleRisk(risks, segment.key))}
                        />
                      ))}
                      {risks.length ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => applyFilters(view, [])}
                          sx={{
                            border: 0,
                            background: "none",
                            cursor: "pointer",
                            fontFamily: "inherit",
                            fontSize: 12.5,
                            color: ui.accent,
                            px: 0.5,
                          }}
                        >
                          Clear
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Search product…"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    sx={{ ...fieldControlSx, width: mobile ? "100%" : 280, m: 0 }}
                  />
                </Box>
              </Box>

              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ tableLayout: "fixed", minWidth: TABLE_MIN_WIDTH }}>
                  <colgroup>
                    {COLUMNS.map((column) => (
                      <col key={column.id} style={column.width ? { width: column.width } : undefined} />
                    ))}
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map((column) => (
                        <TableCell
                          key={column.id}
                          sx={headCellSx(ui, {
                            textAlign: column.num ? "right" : "left",
                            whiteSpace: "nowrap",
                            px: CELL_PX,
                            ...(column.id === "product" ? { pl: 2 } : {}),
                          })}
                        >
                          <Tooltip title={column.tip || ""} placement="top" arrow>
                            {column.sort ? (
                              <TableSortLabel
                                active={sort.field === column.sort}
                                direction={sort.field === column.sort ? sort.dir : "desc"}
                                onClick={() => toggleSort(column.sort)}
                              >
                                {column.label}
                              </TableSortLabel>
                            ) : (
                              <span>{column.label}</span>
                            )}
                          </Tooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={COLUMNS.length} sx={textCellSx(ui, { pl: 2, py: 3 })}>
                          {filtered
                            ? "No products match this search or filter."
                            : "No products with stock or recent sales at this warehouse."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow
                          key={row.product_id}
                          tabIndex={0}
                          selected={drawerProduct?.product_id === row.product_id}
                          onClick={() => setDrawerProduct(row)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") setDrawerProduct(row);
                          }}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { backgroundColor: ui.surfaceAlt },
                            "&.Mui-selected, &.Mui-selected:hover": {
                              backgroundColor: ui.accentSoft,
                            },
                          }}
                        >
                          {COLUMNS.map((column) => (
                            <TableCell
                              key={column.id}
                              sx={
                                column.num
                                  ? numCellSx(ui, {
                                      fontFamily: MONO_FONT,
                                      whiteSpace: "nowrap",
                                      px: CELL_PX,
                                    })
                                  : textCellSx(ui, {
                                      px: CELL_PX,
                                      ...(column.id === "product" ? { pl: 2 } : {}),
                                    })
                              }
                            >
                              {cellValue(row, column)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
              <TablePagination
                component="div"
                count={payload.total || 0}
                page={page}
                onPageChange={(_event, next) => setPage(next)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Box>

            <Box
              sx={{
                ...ui.card,
                mt: 2,
                px: 2,
                py: 1.5,
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {riskSegments.map((segment) => (
                <Box
                  key={segment.key}
                  sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12 }}
                >
                  <Box
                    sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: segment.color }}
                  />
                  <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                    {segment.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography
              sx={{ fontSize: 11.5, color: colors.textTertiary, mt: 1.75, lineHeight: 1.6 }}
            >
              Days of cover = on hand ÷ (30-day units ÷ 30); product Avg Days pools all of the
              product&apos;s SKUs. Risk buckets are per SKU, so a product can have SKUs in several
              buckets; &quot;Needs attention&quot; lists products with at least one Out of Stock,
              Critical or At Risk SKU. Sales exclude cancelled orders and use each order&apos;s
              sold line price. SKUs with no stock and no sales are omitted. Click a product to see
              its SKUs.
              {dataQuality?.sold_not_in_inventory?.skus
                ? ` ${fmtUnits(dataQuality.sold_not_in_inventory.skus)} SKUs sold ${fmtUnits(
                    dataQuality.sold_not_in_inventory.units,
                  )} units here but have no inventory record at this warehouse and are not shown.`
                : ""}
              {summary.long_stockouts
                ? ` ${fmtUnits(summary.long_stockouts)} SKUs have been out of stock for the whole 30-day window after selling before it; with no sales in the window they have no days of cover, so they are not in these buckets — Purchase Order Analysis lists them with their last known sales rate.`
                : ""}
              {dataQuality?.ambiguous_attribution_skus
                ? ` ${fmtUnits(dataQuality.ambiguous_attribution_skus)} SKUs had orders that could not be attributed to a single warehouse.`
                : ""}
            </Typography>
          </>
        )}
      </Box>
      <ProductRiskDrawer
        ui={ui}
        product={drawerProduct}
        onClose={() => setDrawerProduct(null)}
        clientId={storedClientId ? null : clientId}
        warehouseId={warehouseId}
        warehouseLabel={payload?.warehouse?.name || warehouse?.warehouse_code}
      />
    </Box>
  );
};

export default InventoryRiskPage;
